// Canvas is 1600 x 900. Coordinates are the center of each circle in px.
//
// FISH LAYOUT — streamlined & lifelike, with a swimming tail.
//
// KEY UPGRADES vs. previous version:
//  - Back and belly are now smooth PARABOLIC ARCS (fusiform silhouette),
//    NOT three straight rows. The fish body is widest in the middle and
//    tapers toward head and tail.
//  - Body core is still nearly straight (the spine).
//  - Head has snout, upper head, jaw, and an eye placed on the upper front.
//  - Tail circles carry `swayAmp` — a small vertical sway amplitude that
//    increases from the tail wrist to the fluke tips, so the whole tail
//    undulates like a swimming fish. A traveling-wave phase is applied in
//    the render layer (Hero.tsx) using each circle's x position.
//  - No two circles overlap; adjacent circles are tangent or clearly apart.

export type CircleDef = {
  id: number;
  x: number;
  y: number;
  size: number;
  color: string;
  /** Vertical sway amplitude in canvas px. If omitted, this circle does not sway. */
  swayAmp?: number;
};

export const CANVAS_W = 1600;
export const CANVAS_H = 900;

// Blue palette — darker on top (back), lighter on bottom (belly)
const B_DEEP  = "#1E3A8A"; // deep navy (darkest — back, shading)
const B_MAIN  = "#2563EB"; // strong blue
const B_MID   = "#3B82F6"; // mid blue
const B_SOFT  = "#60A5FA"; // soft blue
const B_LIGHT = "#93C5FD"; // light blue (belly highlights)

export const circles: CircleDef[] = [
  // ============================================================
  // HEAD — snout, upper head, jaw, eye
  // ============================================================
  { id: 1,  x: 200, y: 400, size: 60, color: B_MAIN }, // snout / nose (tangent to head core)
  { id: 2,  x: 240, y: 335, size: 30, color: B_DEEP }, // top of head
  { id: 3,  x: 240, y: 465, size: 30, color: B_MID  }, // lower jaw
  { id: 4,  x: 275, y: 335, size: 22, color: "#0B1B4D" }, // EYE (very dark)

  // ============================================================
  // BODY CORE (spine) — nearly straight tangent chain along y = 400
  // Widest in the middle, tapering toward the tail.
  // ============================================================
  { id: 10, x: 280,  y: 400, size: 100, color: B_MAIN }, // head core
  { id: 11, x: 395,  y: 400, size: 130, color: B_DEEP },
  { id: 12, x: 537,  y: 400, size: 155, color: B_MAIN }, // biggest anchor
  { id: 13, x: 695,  y: 400, size: 160, color: B_DEEP }, // biggest anchor
  { id: 14, x: 853,  y: 400, size: 155, color: B_MAIN },
  { id: 15, x: 995,  y: 400, size: 130, color: B_MID,  swayAmp: 2 },
  { id: 16, x: 1110, y: 400, size: 100, color: B_DEEP, swayAmp: 6 },
  { id: 17, x: 1195, y: 400, size:  70, color: B_MAIN, swayAmp: 12 }, // tail wrist
  { id: 18, x: 1255, y: 400, size:  50, color: B_DEEP, swayAmp: 20 }, // very base of tail

  // ============================================================
  // BACK ARC (dorsal silhouette) — parabolic curve, peaks at middle
  // y values: 295 → 250 → 215 → 215 → 245 → 285 → 300
  // ============================================================
  { id: 20, x: 395, y: 295, size: 65,  color: B_MID  },
  { id: 21, x: 470, y: 250, size: 80,  color: B_MAIN },
  { id: 22, x: 560, y: 215, size: 100, color: B_DEEP }, // upper apex
  { id: 23, x: 685, y: 215, size: 105, color: B_MAIN }, // upper apex
  { id: 24, x: 810, y: 245, size: 95,  color: B_DEEP },
  { id: 25, x: 910, y: 285, size: 80,  color: B_MID  },
  { id: 26, x: 985, y: 305, size: 60,  color: B_MAIN },

  // ============================================================
  // BELLY ARC (ventral silhouette) — mirror of back, lighter tones
  // ============================================================
  { id: 30, x: 395, y: 505, size: 65,  color: B_SOFT  },
  { id: 31, x: 470, y: 550, size: 80,  color: B_MID   },
  { id: 32, x: 560, y: 585, size: 100, color: B_LIGHT }, // lower apex
  { id: 33, x: 685, y: 585, size: 105, color: B_SOFT  }, // lower apex
  { id: 34, x: 810, y: 555, size: 95,  color: B_MID   },
  { id: 35, x: 910, y: 515, size: 80,  color: B_LIGHT },
  { id: 36, x: 985, y: 500, size: 60,  color: B_SOFT  },

  // ============================================================
  // DORSAL FIN — sharp triangular fin above the back apex
  // ============================================================
  { id: 40, x: 580, y: 140, size: 45, color: B_DEEP }, // base left
  { id: 41, x: 630, y: 105, size: 40, color: B_MAIN },
  { id: 42, x: 675, y:  82, size: 30, color: B_DEEP }, // PEAK
  { id: 43, x: 720, y: 108, size: 40, color: B_MAIN },
  { id: 44, x: 760, y: 145, size: 45, color: B_MID  }, // base right

  // ============================================================
  // BELLY FIN (pelvic/anal fin) — a small triangle under the belly
  // ============================================================
  { id: 50, x: 605, y: 655, size: 50, color: B_SOFT },
  { id: 51, x: 650, y: 690, size: 55, color: B_MID  }, // lowest
  { id: 52, x: 700, y: 655, size: 45, color: B_LIGHT },

  // ============================================================
  // UPPER TAIL FLUKE — sweeps up-right, sways with body wave
  // ============================================================
  { id: 60, x: 1305, y: 355, size: 55, color: B_MAIN, swayAmp: 24 },
  { id: 61, x: 1355, y: 300, size: 48, color: B_DEEP, swayAmp: 30 },
  { id: 62, x: 1395, y: 245, size: 38, color: B_MID,  swayAmp: 36 },
  { id: 63, x: 1425, y: 195, size: 28, color: B_SOFT, swayAmp: 44 }, // upper TIP
  { id: 64, x: 1330, y: 250, size: 32, color: B_MAIN, swayAmp: 28 }, // inner fill

  // ============================================================
  // LOWER TAIL FLUKE — sweeps down-right, sways with body wave
  // ============================================================
  { id: 70, x: 1305, y: 445, size: 55, color: B_MID,  swayAmp: 24 },
  { id: 71, x: 1355, y: 500, size: 48, color: B_MAIN, swayAmp: 30 },
  { id: 72, x: 1395, y: 555, size: 38, color: B_DEEP, swayAmp: 36 },
  { id: 73, x: 1425, y: 605, size: 28, color: B_SOFT, swayAmp: 44 }, // lower TIP
  { id: 74, x: 1330, y: 550, size: 32, color: B_MID,  swayAmp: 28 }, // inner fill
];

